import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Button, Select, SelectItem, Slider } from '@carbon/react';
import { Pen, Erase, ColorPalette, Reset, Download } from '@carbon/icons-react';

interface DrawingData {
  type: 'draw' | 'erase' | 'clear';
  points?: { x: number; y: number }[];
  color?: string;
  width?: number;
  timestamp: number;
}

interface WhiteboardCanvasProps {
  sessionId: string;
  userId: string;
  onDraw?: (data: DrawingData) => void;
  remoteDrawData?: DrawingData;
}

export const WhiteboardCanvas: React.FC<WhiteboardCanvasProps> = ({ sessionId, userId, onDraw, remoteDrawData }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const contextRef = useRef<CanvasRenderingContext2D | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState<'pen' | 'eraser'>('pen');
  const [color, setColor] = useState('#000000');
  const [lineWidth, setLineWidth] = useState(2);
  const [history, setHistory] = useState<ImageData[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;

    const context = canvas.getContext('2d');
    if (!context) return;

    context.scale(2, 2);
    context.lineCap = 'round';
    context.strokeStyle = color;
    context.lineWidth = lineWidth;
    contextRef.current = context;

    context.fillStyle = 'white';
    context.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  useEffect(() => {
    if (remoteDrawData && contextRef.current) {
      handleRemoteDraw(remoteDrawData);
    }
  }, [remoteDrawData]);

  const handleRemoteDraw = (data: DrawingData) => {
    const context = contextRef.current;
    if (!context) return;

    switch (data.type) {
      case 'draw':
        if (data.points && data.points.length > 1) {
          context.globalCompositeOperation = 'source-over';
          context.strokeStyle = data.color || '#000000';
          context.lineWidth = data.width || 2;
          context.beginPath();
          context.moveTo(data.points[0].x, data.points[0].y);
          data.points.forEach(point => {
            context.lineTo(point.x, point.y);
          });
          context.stroke();
        }
        break;
      case 'erase':
        if (data.points && data.points.length > 1) {
          context.globalCompositeOperation = 'destination-out';
          context.lineWidth = (data.width || 10) * 2;
          context.beginPath();
          context.moveTo(data.points[0].x, data.points[0].y);
          data.points.forEach(point => {
            context.lineTo(point.x, point.y);
          });
          context.stroke();
          context.globalCompositeOperation = 'source-over';
        }
        break;
      case 'clear':
        const canvas = canvasRef.current;
        if (canvas) {
          context.clearRect(0, 0, canvas.width, canvas.height);
          context.fillStyle = 'white';
          context.fillRect(0, 0, canvas.width, canvas.height);
        }
        break;
    }
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || !contextRef.current) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setIsDrawing(true);

    if (tool === 'eraser') {
      contextRef.current.globalCompositeOperation = 'destination-out';
      contextRef.current.lineWidth = lineWidth * 2;
    } else {
      contextRef.current.globalCompositeOperation = 'source-over';
      contextRef.current.strokeStyle = color;
      contextRef.current.lineWidth = lineWidth;
    }

    contextRef.current.beginPath();
    contextRef.current.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !contextRef.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    contextRef.current.lineTo(x, y);
    contextRef.current.stroke();

    if (onDraw) {
      onDraw({
        type: tool === 'eraser' ? 'erase' : 'draw',
        points: [{ x, y }],
        color: tool === 'pen' ? color : undefined,
        width: lineWidth,
        timestamp: Date.now(),
      });
    }
  };

  const stopDrawing = () => {
    if (!isDrawing || !contextRef.current) return;

    setIsDrawing(false);

    if (tool === 'eraser') {
      contextRef.current.globalCompositeOperation = 'source-over';
    }

    saveToHistory();
  };

  const saveToHistory = () => {
    const canvas = canvasRef.current;
    const context = contextRef.current;
    if (!canvas || !context) return;

    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    setHistory(prev => [...prev.slice(-9), imageData]);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const context = contextRef.current;
    if (!canvas || !context) return;

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = 'white';
    context.fillRect(0, 0, canvas.width, canvas.height);

    if (onDraw) {
      onDraw({
        type: 'clear',
        timestamp: Date.now(),
      });
    }

    setHistory([]);
  };

  const downloadCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = `whiteboard-${sessionId}-${Date.now()}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  return (
    <div className="whiteboard-container" style={{ height: '100%' }}>
      <div
        className="whiteboard-toolbar"
        style={{
          display: 'flex',
          gap: '1rem',
          padding: '1rem',
          background: '#f4f4f4',
          alignItems: 'center',
        }}
      >
        <Button kind={tool === 'pen' ? 'primary' : 'ghost'} size="sm" renderIcon={Pen} onClick={() => setTool('pen')}>
          Pen
        </Button>

        <Button
          kind={tool === 'eraser' ? 'primary' : 'ghost'}
          size="sm"
          renderIcon={Erase}
          onClick={() => setTool('eraser')}
        >
          Eraser
        </Button>

        <div style={{ width: '100px' }}>
          <input
            type="color"
            value={color}
            onChange={e => setColor(e.target.value)}
            disabled={tool === 'eraser'}
            style={{ width: '100%', height: '32px' }}
          />
        </div>

        <div style={{ width: '150px' }}>
          <Slider
            min={1}
            max={20}
            value={lineWidth}
            onChange={({ value }) => setLineWidth(value)}
            labelText="Line Width"
            hideTextInput
          />
        </div>

        <Button kind="danger" size="sm" renderIcon={Reset} onClick={clearCanvas}>
          Clear
        </Button>

        <Button kind="ghost" size="sm" renderIcon={Download} onClick={downloadCanvas}>
          Download
        </Button>
      </div>

      <canvas
        ref={canvasRef}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        style={{
          width: '100%',
          height: 'calc(100% - 80px)',
          cursor: tool === 'eraser' ? 'crosshair' : 'default',
          background: 'white',
          border: '1px solid #e0e0e0',
        }}
      />
    </div>
  );
};

export default WhiteboardCanvas;
